import React from 'react';
import PropTypes from 'prop-types';
import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';

import { getListingCityStateString } from 'common/listingHelpers';
import { formatPhoneNumber } from 'common/formatters';
import {
  PROPERTY_SETUP_ACCESS_METHOD_TYPE_CODE,
  PROPERTY_SETUP_ACCESS_METHOD_TYPE_KEY,
  PROPERTY_SETUP_ACCESS_METHOD_TYPE_SUNRISE_KEY,
  PROPERTY_SETUP_ACCESS_METHOD_TYPE_SUNRISE_NO_KEY,
  PROPERTY_SETUP_ACCESS_TYPE_CONTACT_FOR_KEY,
  PROPERTY_SETUP_ACCESS_TYPE_HIDDEN,
  PROPERTY_SETUP_ACCESS_TYPE_SUPRA_LOCKBOX,
} from 'common/marketingPartnerListingConstants';

import SrBox from 'client/components/SrBox';
import SrCard from 'client/components/SrCard/SrCard';
import SrFlex from 'client/components/SrFlex';
import SrText from 'client/components/SrText';

const SrSunSetupOrderDetails = ({
  listing,
  partnerDisplayName,
  partnerLogo,
  sunlock,
  shouldDisplayAddress,
  shouldDisplayPromiseDate,
}) => {
  const { occupantType, occupantName, occupantPhone, occupantEmail, vacantDate } = listing;

  const _getAccessMethod = () => {
    switch (listing.accessMethod) {
      case PROPERTY_SETUP_ACCESS_METHOD_TYPE_SUNRISE_KEY:
        return 'Key is at the property';
      case PROPERTY_SETUP_ACCESS_METHOD_TYPE_SUNRISE_NO_KEY:
        return 'Key is not at the property';
      case PROPERTY_SETUP_ACCESS_METHOD_TYPE_CODE:
        return 'Access Code';
      case PROPERTY_SETUP_ACCESS_METHOD_TYPE_KEY:
        return 'Key';
      default:
        return listing.accessMethod;
    }
  };

  const _getPropertyAccessType = () => {
    const result = [];

    if (listing.propertyAccessCode) {
      result.push(`Code: ${listing.propertyAccessCode}`);
    }
    if (listing.lockboxCode) {
      result.push(`Combination Lockbox: ${listing.lockboxCode}`);
    }

    switch (listing.propertySetupAccessType) {
      case PROPERTY_SETUP_ACCESS_TYPE_CONTACT_FOR_KEY:
        result.push(
          'Contact Person for Keys:',
          `Contact Name: ${listing.propertyAccessContactName}`,
          `Contact Phone: ${listing.propertyAccessContactPhone}`,
          `Contact Email : ${listing.propertyAccessContactEmail}`,
        );
        break;

      case PROPERTY_SETUP_ACCESS_TYPE_HIDDEN:
        result.push(
          'Key is hidden:',
          `Contact Name: ${listing.propertyAccessContactName}`,
          `Contact Phone: ${listing.propertyAccessContactPhone}`,
          `Contact Email : ${listing.propertyAccessContactEmail}`,
        );
        break;

      case PROPERTY_SETUP_ACCESS_TYPE_SUPRA_LOCKBOX:
        result.push('Supra Lockbox');
        break;
    }
    return result.join('\n');
  };

  const _hasDefaultSunroomLogo = () => {
    if (!partnerLogo) {
      return true;
    }

    return partnerLogo.includes('sunroom.png');
  };

  const accessMethod = _getAccessMethod();
  const accessType = _getPropertyAccessType();
  const hasDefaultSunroomLogo = _hasDefaultSunroomLogo();

  const showHeader = shouldDisplayAddress || shouldDisplayPromiseDate;

  return (
    <SrCard>
      <SrFlex justify="between" padding="0px 16px">
        {shouldDisplayAddress && (
          <SrBox>
            <SrBox>
              <SrText fontSize="21px" weight="800" font="greycliff">
                {listing.addressStreetDisplay}
              </SrText>
            </SrBox>
            <SrBox marginTop="13px">
              <SrText fontSize="21px" weight="800" font="greycliff">
                {getListingCityStateString(listing)}
              </SrText>
            </SrBox>
          </SrBox>
        )}
        {shouldDisplayPromiseDate && (
          <SrBox>
            <SrBox>
              <SrText fontSize="21px" weight="800" font="greycliff">
                Promise Date
              </SrText>
            </SrBox>
            <SrBox>
              <SrText fontSize="18px" weight="600" font="greycliff">
                {momentWithAustinTimezone(listing.promiseDate).format('MMM DD')}
              </SrText>
            </SrBox>
          </SrBox>
        )}
      </SrFlex>
      <SrFlex marginTop={showHeader ? '40px' : ''} justify="between" wrap="wrap" padding="0px 16px">
        <SrBox marginTop="8px" maxWidth="200px">
          <SrBox>
            <SrText weight="800" fontSize="16px" font="greycliff">
              Property Access Method
            </SrText>
          </SrBox>
          <SrBox>
            <SrText fontSize="16px" weight="500" font="greycliff">
              {accessMethod}
            </SrText>
          </SrBox>
          <SrBox>
            <SrText fontSize="14px" weight="500" lineHeight="24px" font="greycliff">
              {accessType}
            </SrText>
          </SrBox>
          {listing.lockboxLocation && (
            <SrBox>
              <SrText fontSize="14px" weight="500" font="greycliff">
                Lockbox Location: {listing.lockboxLocation}
              </SrText>
            </SrBox>
          )}
          {listing.dateKeyExpectedAtListing && (
            <SrBox>
              <SrText fontSize="14px" weight="500" font="greycliff">
                Date expected at listing:{' '}
                {momentWithAustinTimezone(listing.dateKeyExpectedAtListing).format('MMM DD')}
              </SrText>
            </SrBox>
          )}
        </SrBox>
        <SrBox marginTop="8px" maxWidth="200px">
          <SrBox>
            <SrText weight="800" fontSize="16px" font="greycliff">
              Occupant Type
            </SrText>
          </SrBox>
          <SrBox>
            <SrText fontSize="16px" weight="500" font="greycliff">
              {occupantType || 'No occupant type'}
            </SrText>
            {vacantDate && (
              <SrBox>
                <SrText fontSize="16px" weight="500" font="greycliff">
                  Vacancy Date: {momentWithAustinTimezone(vacantDate).format('MMM DD')}
                </SrText>
              </SrBox>
            )}
            {occupantName && (
              <SrBox>
                <SrText fontSize="16px" weight="500" font="greycliff">
                  {occupantName.split(';')[0]}
                </SrText>
              </SrBox>
            )}
            {occupantPhone && (
              <SrBox>
                <SrText fontSize="16px" weight="500" font="greycliff">
                  {formatPhoneNumber(occupantPhone)}
                </SrText>
              </SrBox>
            )}
            {occupantEmail && (
              <SrBox>
                <SrText fontSize="16px" weight="500" font="greycliff">
                  {occupantEmail}
                </SrText>
              </SrBox>
            )}
          </SrBox>
        </SrBox>
        <SrBox marginTop="8px" maxWidth="200px">
          <SrBox>
            <SrText weight="800" fontSize="16px" font="greycliff">
              Gate Code
            </SrText>
          </SrBox>
          <SrBox>
            <SrText fontSize="16px" weight="500" font="greycliff">
              {listing.gateCode || 'None'}
            </SrText>
          </SrBox>
        </SrBox>
        <SrBox marginTop="8px" maxWidth="200px">
          <SrBox>
            <SrText weight="800" fontSize="16px" font="greycliff">
              Alarm Code
            </SrText>
          </SrBox>
          <SrBox>
            <SrText fontSize="16px" weight="500" font="greycliff">
              {listing.alarmCode || 'None'}
            </SrText>
          </SrBox>
        </SrBox>
      </SrFlex>
      {sunlock && (
        <SrFlex marginTop="32px" padding="0px 16px" gap="30px">
          <SrBox marginTop="8px" maxWidth="200px">
            <SrBox>
              <SrText weight="800" fontSize="16px" font="greycliff">
                Sunlock Master Code
              </SrText>
            </SrBox>
            <SrBox>
              <SrText fontSize="16px" weight="500" font="greycliff">
                {sunlock.partnerLockCode}
              </SrText>
            </SrBox>
          </SrBox>
          <SrBox marginTop="8px" maxWidth="200px">
            <SrBox>
              <SrText weight="800" fontSize="16px" font="greycliff">
                Sunlock Backup Code
              </SrText>
            </SrBox>
            <SrBox>
              <SrText fontSize="16px" weight="500" font="greycliff">
                {sunlock.lockCode}
              </SrText>
            </SrBox>
          </SrBox>
        </SrFlex>
      )}
      <SrFlex justify="between" marginTop="32px" padding="0px 16px">
        <SrBox width="calc(33% - 12px)">
          <SrBox>
            <SrText weight="800" fontSize="16px" font="greycliff">
              Special Showing Instructions
            </SrText>
          </SrBox>
          <SrBox>
            <SrText fontSize="16px" weight="500" font="greycliff">
              {listing.showingInstructions || 'None'}
            </SrText>
          </SrBox>
        </SrBox>
        <SrBox width="calc(33% - 12px)">
          <SrBox>
            <SrText weight="800" fontSize="16px" font="greycliff">
              Special Notes
            </SrText>
          </SrBox>
          <SrBox>
            <SrText fontSize="16px" weight="500" font="greycliff">
              {listing.partnerMarketingNotes || 'None'}
            </SrText>
          </SrBox>
        </SrBox>
        <SrBox width="calc(33% - 12px)">
          <SrBox>
            <SrText weight="800" fontSize="16px" font="greycliff">
              Property Manager
            </SrText>
          </SrBox>
          <SrBox>
            <SrText fontSize="16px" weight="500" font="greycliff">
              {partnerDisplayName || 'Missing Name'}
            </SrText>
          </SrBox>
          {!hasDefaultSunroomLogo && (
            <SrBox
              marginTop="8px"
              backgroundImage={`url(${partnerLogo})`}
              backgroundSize="contain"
              backgroundRepeat="no-repeat"
              backgroundPosition="center"
              height="44px"
              width="44px"
            />
          )}
        </SrBox>
      </SrFlex>
    </SrCard>
  );
};

SrSunSetupOrderDetails.propTypes = {
  listing: PropTypes.object.isRequired,
  partnerDisplayName: PropTypes.string,
  shouldDisplayAddress: PropTypes.bool,
  shouldDisplayPromiseDate: PropTypes.bool,
};

SrSunSetupOrderDetails.defaultProps = {
  shouldDisplayAddress: true,
  shouldDisplayPromiseDate: true,
};

SrSunSetupOrderDetails.displayName = 'SrSunSetupOrderDetails';

export default SrSunSetupOrderDetails;
